import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Layout";
import { PageHeader, Breadcrumbs } from "@/components/Bits";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, X, RotateCw, Plus, Trash2, Lightbulb } from "lucide-react";

const norm = (s) => (s || "").trim().toLowerCase().replace(/ß/g, "ss");

function DerDieDas() {
  const [items, setItems] = useState([]);
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState(null);
  useEffect(() => { api.get("/tools/der-die-das").then(({ data }) => setItems(data)); }, []);
  const item = items[i];
  if (!item) return <p className="text-muted-foreground">Cargando...</p>;
  const correct = picked === item.article;
  return (
    <div className="max-w-lg" data-testid="tool-der-die-das">
      <p className="text-sm text-muted-foreground mb-2">Palabra {i + 1} de {items.length}</p>
      <div className="rounded-lg border border-border bg-card p-8 text-center">
        <p className="font-display font-black text-3xl">{item.word}</p>
        <p className="text-sm text-muted-foreground">{item.es}</p>
        <div className="mt-6 grid grid-cols-3 gap-2">
          {["der", "die", "das"].map((a) => {
            const right = picked && a === item.article, wrong = picked === a && a !== item.article;
            return <button key={a} disabled={!!picked} onClick={() => setPicked(a)} data-testid={`ddd-${a}`}
              className={`py-3 rounded-md border font-display font-semibold transition-colors ${right ? "border-emerald-500 bg-emerald-500/10" : wrong ? "border-destructive bg-destructive/10" : "border-border hover:border-primary/50"}`}>{a}</button>;
          })}
        </div>
        {picked && (
          <div className={`mt-4 rounded-md p-3 text-sm text-left ${correct ? "bg-emerald-500/10" : "bg-amber-500/10"}`}>
            <p className="font-semibold">{correct ? "¡Correcto!" : `Es "${item.article} ${item.word}"`}</p>
            {item.hint && <p className="text-muted-foreground mt-1">{item.hint}</p>}
          </div>
        )}
      </div>
      {picked && <Button className="mt-4 rounded-full" onClick={() => { setPicked(null); setI((i + 1) % items.length); }}>Siguiente palabra</Button>}
    </div>
  );
}

function Comparator() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/tools/expression-comparison").then(({ data }) => setRows(data)); }, []);
  return (
    <div className="overflow-x-auto rounded-lg border border-border" data-testid="tool-comparador">
      <table className="w-full text-sm">
        <thead className="bg-secondary"><tr>{["Alemán estándar", "Habitual en Suiza", "Suizo coloquial", "Contexto", "Formalidad"].map((h) => <th key={h} className="text-left p-3 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i} className="border-t border-border">
            <td className="p-3 font-medium">{r.standard}</td><td className="p-3 text-accent font-medium">{r.swiss_common}</td>
            <td className="p-3">{r.swiss_dialect}{r.verified && <Check size={12} className="inline ml-1 text-emerald-600" title="Verificado" />}</td>
            <td className="p-3 text-muted-foreground">{r.context}</td><td className="p-3 text-muted-foreground">{r.formality}</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

function WordOrder() {
  const [items, setItems] = useState([]);
  const [i, setI] = useState(0);
  const [ordered, setOrdered] = useState([]);
  const [pool, setPool] = useState([]);
  const [checked, setChecked] = useState(false);
  useEffect(() => { api.get("/tools/word-order").then(({ data }) => { setItems(data); }); }, []);
  useEffect(() => { if (items[i]) { setPool([...items[i].words].sort(() => Math.random() - 0.5)); setOrdered([]); setChecked(false); } }, [items, i]);
  const item = items[i];
  if (!item) return <p className="text-muted-foreground">Cargando...</p>;
  const correct = ordered.join(" ") === item.answer;
  return (
    <div className="max-w-xl" data-testid="tool-word-order">
      <p className="text-sm text-muted-foreground mb-3">Traduce: <strong className="text-foreground">{item.es}</strong></p>
      <div className="min-h-[52px] flex flex-wrap gap-2 rounded-md border border-dashed border-border p-3 mb-3">
        {ordered.map((w, k) => <button key={k} disabled={checked} onClick={() => { setOrdered(ordered.filter((_, j) => j !== k)); setPool([...pool, w]); }} className="px-3 py-1.5 rounded-md bg-primary/10 border border-primary/30 text-sm font-medium">{w}</button>)}
        {!ordered.length && <span className="text-sm text-muted-foreground self-center">Toca las palabras en orden</span>}
      </div>
      <div className="flex flex-wrap gap-2 mb-4">{pool.map((w, k) => <button key={k} disabled={checked} onClick={() => { setOrdered([...ordered, w]); setPool(pool.filter((_, j) => j !== k)); }} className="px-3 py-1.5 rounded-md border border-border bg-secondary text-sm font-medium hover:border-primary/50">{w}</button>)}</div>
      {!checked ? <Button disabled={ordered.length !== item.words.length} onClick={() => setChecked(true)}>Comprobar</Button> : (
        <div>
          <div className={`rounded-md p-3 text-sm ${correct ? "bg-emerald-500/10" : "bg-amber-500/10"}`}>
            <p className="font-semibold flex items-center gap-1">{correct ? <><Check size={14} className="text-emerald-600" />¡Correcto!</> : <><Lightbulb size={14} className="text-amber-600" />Solución: {item.answer}</>}</p>
            <p className="text-muted-foreground mt-1">{item.explanation}</p>
          </div>
          <Button className="mt-3 rounded-full" onClick={() => setI((i + 1) % items.length)}>Siguiente frase</Button>
        </div>
      )}
    </div>
  );
}

function VocabProfession() {
  const [profs, setProfs] = useState([]);
  const [sel, setSel] = useState(null);
  const [vocab, setVocab] = useState([]);
  const LABELS = { hosteleria: "Hostelería", limpieza: "Limpieza", construccion: "Construcción", logistica: "Logística", cuidados: "Cuidados", comercio: "Comercio" };
  useEffect(() => { api.get("/tools/vocab-profession").then(({ data }) => setProfs(data.professions)); }, []);
  const load = (p) => { setSel(p); api.get(`/tools/vocab-profession?profession=${p}`).then(({ data }) => setVocab(data.vocab)); };
  return (
    <div data-testid="tool-vocab-profession">
      <div className="flex flex-wrap gap-2 mb-6">{profs.map((p) => <button key={p} onClick={() => load(p)} className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${sel === p ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-secondary"}`}>{LABELS[p] || p}</button>)}</div>
      {vocab.length > 0 && <div className="grid sm:grid-cols-2 gap-2 max-w-2xl">{vocab.map((v, i) => <div key={i} className="rounded-md border border-border bg-card p-4"><p className="font-display font-semibold">{v.de}</p><p className="text-sm text-muted-foreground">{v.es}</p></div>)}</div>}
      {!sel && <p className="text-sm text-muted-foreground">Elige un sector para ver su vocabulario.</p>}
    </div>
  );
}

function Phrases() {
  const [sits, setSits] = useState([]);
  const [sel, setSel] = useState(null);
  const [phrases, setPhrases] = useState([]);
  const LABELS = { transporte: "Transporte", trabajo: "Trabajo", vivienda: "Vivienda", farmacia: "Farmacia", medico: "Médico", banco: "Banco", administracion: "Administración" };
  useEffect(() => { api.get("/tools/phrases").then(({ data }) => setSits(data.situations)); }, []);
  const load = (s) => { setSel(s); api.get(`/tools/phrases?situation=${s}`).then(({ data }) => setPhrases(data.phrases)); };
  return (
    <div data-testid="tool-phrases">
      <div className="flex flex-wrap gap-2 mb-6">{sits.map((s) => <button key={s} onClick={() => load(s)} className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${sel === s ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-secondary"}`}>{LABELS[s] || s}</button>)}</div>
      {phrases.map((p, i) => <div key={i} className="rounded-md bg-secondary p-4 mb-2 max-w-xl"><p className="font-medium">{p.de}</p><p className="text-sm text-muted-foreground">{p.es}</p></div>)}
      {!sel && <p className="text-sm text-muted-foreground">Elige una situación para ver frases útiles.</p>}
    </div>
  );
}

function StudyPlan() {
  const [f, setF] = useState({ minutes_per_day: 15, days_per_week: 5, target_words: 300 });
  const [res, setRes] = useState(null);
  const calc = async () => { const { data } = await api.post("/tools/streak-plan", f); setRes(data); };
  const input = "w-full h-11 rounded-md border border-input bg-background px-3 text-sm";
  return (
    <div className="max-w-md" data-testid="tool-study-plan">
      <div className="space-y-4">
        <div><label className="block text-sm font-medium mb-1.5">Minutos al día</label><input type="number" className={input} value={f.minutes_per_day} onChange={(e) => setF({ ...f, minutes_per_day: +e.target.value })} /></div>
        <div><label className="block text-sm font-medium mb-1.5">Días por semana</label><input type="number" min={1} max={7} className={input} value={f.days_per_week} onChange={(e) => setF({ ...f, days_per_week: +e.target.value })} /></div>
        <div><label className="block text-sm font-medium mb-1.5">Meta de palabras</label><input type="number" className={input} value={f.target_words} onChange={(e) => setF({ ...f, target_words: +e.target.value })} /></div>
        <Button onClick={calc} className="rounded-full">Calcular mi plan</Button>
      </div>
      {res && <div className="mt-6 rounded-lg border border-border bg-card p-5"><p className="font-display font-bold text-lg mb-2">{res.weekly_minutes} min/semana</p><p className="text-sm">{res.message}</p><p className="text-xs text-muted-foreground mt-2">{res.note}</p></div>}
    </div>
  );
}

function Dictionary() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [f, setF] = useState({ de: "", es: "", note: "" });
  const load = () => api.get("/tools/dictionary").then(({ data }) => setEntries(data));
  useEffect(() => { if (user) load(); }, [user]);
  if (!user) return <div className="rounded-lg border border-border bg-card p-6 max-w-md"><p className="text-sm text-muted-foreground mb-4">Crea una cuenta gratis para guardar tu diccionario personal.</p><Link to="/registro"><Button className="rounded-full">Crear cuenta</Button></Link></div>;
  const add = async (e) => { e.preventDefault(); await api.post("/tools/dictionary", f); setF({ de: "", es: "", note: "" }); toast.success("Palabra guardada"); load(); };
  const del = async (de) => { await api.delete(`/tools/dictionary/${encodeURIComponent(de)}`); load(); };
  const input = "h-11 rounded-md border border-input bg-background px-3 text-sm";
  return (
    <div className="max-w-2xl" data-testid="tool-dictionary">
      <form onSubmit={add} className="grid sm:grid-cols-4 gap-2 mb-6">
        <input required placeholder="Alemán" className={input} value={f.de} onChange={(e) => setF({ ...f, de: e.target.value })} />
        <input required placeholder="Español" className={input} value={f.es} onChange={(e) => setF({ ...f, es: e.target.value })} />
        <input placeholder="Nota (opcional)" className={input} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        <Button type="submit"><Plus size={16} className="mr-1" />Añadir</Button>
      </form>
      {entries.length ? <div className="grid sm:grid-cols-2 gap-2">{entries.map((en, i) => <div key={i} className="rounded-md border border-border bg-card p-4 flex justify-between items-start"><div><p className="font-display font-semibold">{en.de}</p><p className="text-sm text-muted-foreground">{en.es}</p>{en.note && <p className="text-xs italic text-muted-foreground mt-1">{en.note}</p>}</div><button onClick={() => del(en.de)} className="text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button></div>)}</div> : <p className="text-sm text-muted-foreground">Aún no has guardado palabras.</p>}
    </div>
  );
}

const REGISTRY = {
  "der-die-das": { t: "Entrenador der/die/das", d: "Adivina el artículo y aprende la lógica detrás de cada palabra.", C: DerDieDas },
  "comparador": { t: "Comparador de expresiones", d: "Alemán estándar, forma habitual en Suiza y contexto de uso.", C: Comparator },
  "orden-de-palabras": { t: "Orden de palabras", d: "Coloca cada palabra en su sitio y domina la estructura de la frase.", C: WordOrder },
  "vocabulario-profesion": { t: "Vocabulario por profesión", d: "Palabras clave de tu sector, con traducción.", C: VocabProfession },
  "frases": { t: "Frases para situaciones", d: "Frases listas para el día a día en Suiza.", C: Phrases },
  "plan-de-estudio": { t: "Plan de estudio y racha", d: "Calcula un ritmo realista para tu objetivo.", C: StudyPlan },
  "diccionario": { t: "Diccionario personal", d: "Tu vocabulario, guardado en tu cuenta.", C: Dictionary },
};

export default function ToolDetail() {
  const { slug } = useParams();
  const tool = REGISTRY[slug];
  if (!tool) return <PageHeader title="Herramienta no encontrada" />;
  const C = tool.C;
  return (
    <>
      <Seo title={tool.t} description={tool.d} noindex />
      <PageHeader overline="Herramienta gratuita" title={tool.t} subtitle={tool.d} />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: "Herramientas", to: "/herramientas" }, { label: tool.t }]} />
        <C />
      </div>
    </>
  );
}
